// pages/integrations/EmailIntegrationPage.tsx
import React from "react";
import { Mail, Database, Key, Check, Copy } from "lucide-react";
import useAuth from "../../../../hooks/features/auth/useAuth";
import { Entreprise } from "../../../../types/features/Entreprise";
import {
  IntegrationHeader,
  ApiKeyTable,
  ApiKeyModal,
  ApiDeleteConfirmationModal,
  NewKeyNotification,
  CodeExamples,
  ApiTester,
  FeatureList,
} from "../../../../components/features/integrations";

import { useApiKeyModule } from "../../../../hooks/features/apiKeys/useApiKeyModule";
import type { EndpointExample } from "../../../../components/features/integrations/CodeExamples";
import type { TesterEndpoint } from "../../../../components/features/integrations/ApiTester";

const MODULE = "email";

export const EmailIntegrationPage: React.FC = () => {
  const { user } = useAuth();
  const entreprise = user?.profile as Entreprise;
  const originUrl = window.location.origin;

  const {
    apiKeys,
    moduleKey,
    loading,
    copied,
    isModalOpen,
    keyToDelete,
    isRegenerating,
    newKeySecret,
    formData,
    setFormData,
    setIsModalOpen,
    setKeyToDelete,
    setNewKeySecret,
    copyToClipboard,
    handleCreateKey,
    handleRegenerate,
    handleDeleteKey,
    handleToggleKey,
  } = useApiKeyModule(MODULE);

  const config = {
    companyId: entreprise?.id || "",
    apiKey: moduleKey?.key || "Aucune clé API trouvée",
    baseUrl: `${originUrl}/api/v1/email`,
  };

  // ✅ Endpoints pour CodeExamples (URL complète)
  const codeEndpoints: EndpointExample[] = [
    {
      key: "email-otp",
      label: "OTP",
      method: "POST",
      endpoint: `${config.baseUrl}/send`,
      body: {
        entreprise_id: config.companyId,
        to: "foussdev2004@gmail.com",
        template: "otp",
        variables: {
          title: "Votre code de vérification",
          message: "Utilisez le code ci-dessous pour valider votre identité.",
          code: "123456",
          footer: "Ce code expire dans 10 minutes.",
          company_name: "Pythasoft",
        },
      },
    },
    {
      key: "email-confirmation",
      label: "Confirmation",
      method: "POST",
      endpoint: `${config.baseUrl}/send`,
      body: {
        entreprise_id: config.companyId,
        to: "client@example.com",
        template: "confirmation",
        variables: {
          title: "Confirmez votre adresse email",
          message: "Cliquez sur le bouton ci-dessous pour confirmer votre adresse.",
          link: "https://pythasoft.com/confirm?token=abc123",
          cta_text: "Confirmer mon email",
          company_name: "Pythasoft",
        },
      },
    },
    {
      key: "email-reset",
      label: "Reset password",
      method: "POST",
      endpoint: `${config.baseUrl}/send`,
      body: {
        entreprise_id: config.companyId,
        to: "client@example.com",
        template: "reset_password",
        variables: {
          title: "Réinitialisez votre mot de passe",
          message: "Cliquez sur le bouton ci-dessous pour choisir un nouveau mot de passe.",
          link: "https://pythasoft.com/reset?token=xyz789",
          cta_text: "Réinitialiser mon mot de passe",
          company_name: "Pythasoft",
        },
      },
    },
    {
      key: "email-welcome",
      label: "Bienvenue",
      method: "POST",
      endpoint: `${config.baseUrl}/send`,
      body: {
        entreprise_id: config.companyId,
        to: "client@example.com",
        template: "welcome",
        variables: {
          title: "Bienvenue chez Pythasoft",
          message: "Merci de votre inscription. Voici comment démarrer :",
          steps: ["Complétez votre profil", "Découvrez votre dashboard", "Invitez votre équipe"],
          link: "https://app.pythasoft.com/dashboard",
          cta_text: "Accéder à mon espace",
          company_name: "Pythasoft",
        },
      },
    },
  ];

  // ✅ Endpoints pour ApiTester (chemin relatif)
  // Note : pour "welcome", steps est passé en string CSV (sera splitté côté backend)
  const testerEndpoints: TesterEndpoint[] = [
    {
      key: "email-otp",
      label: "OTP",
      method: "POST",
      endpoint: "/v1/email/send",
      body: {
        to: "foussdev2004@gmail.com",
        template: "otp",
        variables: {
          title: "Votre code de vérification",
          message: "Utilisez le code ci-dessous pour valider votre identité.",
          code: "123456",
          footer: "Ce code expire dans 10 minutes.",
          company_name: "Pythasoft",
        },
      },
    },
    {
      key: "email-confirmation",
      label: "Confirmation",
      method: "POST",
      endpoint: "/v1/email/send",
      body: {
        to: "foussdev2004@gmail.com",
        template: "confirmation",
        variables: {
          title: "Confirmez votre adresse email",
          message: "Cliquez sur le bouton ci-dessous pour confirmer votre adresse.",
          link: "https://pythasoft.com/confirm?token=abc123",
          cta_text: "Confirmer mon email",
          company_name: "Pythasoft",
        },
      },
    },
    {
      key: "email-reset",
      label: "Reset password",
      method: "POST",
      endpoint: "/v1/email/send",
      body: {
        to: "foussdev2004@gmail.com",
        template: "reset_password",
        variables: {
          title: "Réinitialisez votre mot de passe",
          message: "Cliquez sur le bouton ci-dessous pour choisir un nouveau mot de passe.",
          link: "https://pythasoft.com/reset?token=xyz789",
          cta_text: "Réinitialiser mon mot de passe",
          company_name: "Pythasoft",
        },
      },
    },
    {
      key: "email-welcome",
      label: "Bienvenue",
      method: "POST",
      endpoint: "/v1/email/send",
      body: {
        to: "foussdev2004@gmail.com",
        template: "welcome",
        variables: {
          title: "Bienvenue chez Pythasoft",
          message: "Merci de votre inscription. Voici comment démarrer :",
          steps: "Complétez votre profil, Découvrez votre dashboard, Invitez votre équipe",
          link: "https://app.pythasoft.com/dashboard",
          cta_text: "Accéder à mon espace",
          company_name: "Pythasoft",
        },
      },
    },
  ];
  const features = [
    "Templates transactionnels (OTP, confirmation…)",
    "Personnalisation par entreprise",
    "Fallback texte automatique",
    "Compatible Gmail / Outlook",
    "Statistiques de délivrance",
  ];

  if (!entreprise) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <IntegrationHeader
        icon={<Mail size={40} className="text-blue-600 flex-shrink-0" />}
        title="Email"
        description="Envoyez des emails transactionnels à vos clients depuis votre dashboard ou via l'API"
        status="Actif"
        statusColor="green"
        gradientFrom="blue-50"
        gradientTo-indigo-50
      />

      {/* Section spécifique Email */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <Database size={20} className="text-blue-600" />
            <h3 className="font-semibold text-slate-800">Intégration Email</h3>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Envoyez des emails transactionnels à vos clients (OTP, confirmation, reset password, bienvenue…).
          </p>

          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">ID Entreprise</label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-600">
                {config.companyId}
              </code>
              <button
                onClick={() => copyToClipboard(config.companyId, "companyId")}
                className="p-2.5 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
              >
                {copied === "companyId" ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Copy size={18} className="text-slate-600" />
                )}
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Clé API Email</label>
              <button
                onClick={() => setIsModalOpen(true)}
                className="p-1 text-primary hover:bg-primary/10 rounded transition-colors"
              >
                <Key size={16} />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-600 break-all">
                {config.apiKey}
              </code>
              <button
                onClick={() => copyToClipboard(config.apiKey, "apiKey")}
                className="p-2.5 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
              >
                {copied === "apiKey" ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Copy size={18} className="text-slate-600" />
                )}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {loading ? "Chargement..." : "Cette clé est utilisée pour les appels API du module Email"}
            </p>
          </div>
        </div>
      </div>

      {/* ✅ Exemples de code multi-endpoints */}
      <CodeExamples apiKey={config.apiKey} endpoints={codeEndpoints} />

      {/* ✅ Testeur API multi-endpoints */}
      <ApiTester endpoints={testerEndpoints} apiKey={config.apiKey} companyId={config.companyId} moduleName={MODULE} />

      {/* Gestion des clés API */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key size={20} className="text-amber-600" />
              <h3 className="font-semibold text-slate-800">Gestion des clés API</h3>
              <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-medium">
                {apiKeys.length} clé(s)
              </span>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              <Key size={16} />
              Nouvelle clé
            </button>
          </div>
        </div>

        <div className="p-5">
          <ApiKeyTable
            apiKeys={apiKeys}
            loading={loading}
            moduleLabels={{
              tickets: "Tickets",
              calls: "Appels",
              email: "Email",
              whatsapp: "WhatsApp",
              sms: "SMS",
              webhook: "Webhook",
              all: "Accès complet",
            }}
            onCopy={copyToClipboard}
            onToggle={handleToggleKey}
            onRegenerate={handleRegenerate}
            onDelete={setKeyToDelete}
            onCreateNew={() => setIsModalOpen(true)}
            copiedId={copied}
            isRegenerating={isRegenerating}
            moduleFilter={MODULE}
          />
        </div>
      </div>

      {/* Features */}
      <FeatureList features={features} />

      {/* Modales */}
      <ApiKeyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateKey}
        formData={formData}
        setFormData={setFormData}
        moduleName={MODULE}
        permissions={["create", "read", "update", "delete", "send"]}
        moduleLabels={{
          tickets: "Tickets",
          calls: "Appels",
          email: "Email",
          whatsapp: "WhatsApp",
          sms: "SMS",
          webhook: "Webhook",
          all: "Accès complet",
        }}
      />

      <ApiDeleteConfirmationModal
        keyToDelete={keyToDelete}
        onConfirm={handleDeleteKey}
        onCancel={() => setKeyToDelete(null)}
      />

      <NewKeyNotification newKey={newKeySecret} onClose={() => setNewKeySecret(null)} onCopy={copyToClipboard} />
    </div>
  );
};
